import { useCallback, useEffect, useRef, useState } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { imgUrl } from "../../lib/api";

const Lightbox = ({ items, index, onClose, onNav }) => {
  const touch = useRef(null);
  useEffect(() => {
    const k = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") onNav(1);
      if (e.key === "ArrowLeft") onNav(-1);
    };
    document.addEventListener("keydown", k);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", k);
      document.body.style.overflow = "";
    };
  }, [onClose, onNav]);
  const it = items[index];
  const btn = "absolute top-1/2 -translate-y-1/2 flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-white backdrop-blur transition-colors hover:bg-white/20";
  return (
    <div
      data-testid="gallery-lightbox"
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[60] flex items-center justify-center bg-forest/95 p-4"
      onClick={onClose}
      onTouchStart={(e) => (touch.current = e.touches[0].clientX)}
      onTouchEnd={(e) => {
        const dx = e.changedTouches[0].clientX - (touch.current ?? 0);
        if (Math.abs(dx) > 50) onNav(dx < 0 ? 1 : -1);
      }}
    >
      <img src={imgUrl(it.url)} alt={it.alt} className="max-h-[82vh] max-w-full rounded-xl object-contain shadow-2xl" onClick={(e) => e.stopPropagation()} />
      <button className="absolute right-4 top-4 flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20" onClick={onClose} aria-label="Kapat" data-testid="lightbox-close">
        <X className="h-5 w-5" />
      </button>
      {items.length > 1 && (
        <>
          <button className={`${btn} left-3`} onClick={(e) => { e.stopPropagation(); onNav(-1); }} aria-label="Önceki" data-testid="lightbox-prev"><ChevronLeft className="h-5 w-5" /></button>
          <button className={`${btn} right-3`} onClick={(e) => { e.stopPropagation(); onNav(1); }} aria-label="Sonraki" data-testid="lightbox-next"><ChevronRight className="h-5 w-5" /></button>
        </>
      )}
      <p className="absolute bottom-5 left-0 right-0 text-center text-xs tracking-wider text-white/60">{it.category} · {index + 1} / {items.length}</p>
    </div>
  );
};

export const GalleryGrid = ({ items }) => {
  const [idx, setIdx] = useState(null);
  const nav = useCallback((d) => setIdx((i) => (i + d + items.length) % items.length), [items.length]);
  const close = useCallback(() => setIdx(null), []);
  return (
    <>
      <div className="masonry" data-testid="gallery-grid">
        {items.map((g, i) => (
          <button
            key={g.id || g.url}
            type="button"
            onClick={() => setIdx(i)}
            data-testid={`gallery-item-${i}`}
            className="group relative block w-full overflow-hidden rounded-2xl ring-1 ring-gold/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold"
          >
            <img src={imgUrl(g.url)} alt={g.alt} loading="lazy" decoding="async" className="w-full transition-transform duration-700 group-hover:scale-[1.04]" />
            <span className="absolute inset-0 flex items-end bg-gradient-to-t from-emerald-950/60 via-transparent to-transparent p-4 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
              <span className="text-xs font-medium uppercase tracking-[0.18em] text-white">{g.category}</span>
            </span>
          </button>
        ))}
      </div>
      {idx !== null && items[idx] && <Lightbox items={items} index={idx} onClose={close} onNav={nav} />}
    </>
  );
};
